import { useEffect, useRef, useState } from "react";
import { api, API, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Download, Trash2, Upload, Send } from "lucide-react";

const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

// Reusable attachments panel for a parent entity (task | project | note).
export default function Attachments({ parentType, parentId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = () =>
    api.get("/files", { params: { parent_type: parentType, parent_id: parentId } }).then((r) => setFiles(r.data));
  useEffect(() => {
    if (parentId) load();
  }, [parentType, parentId]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Max file size is 20 MB.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("parent_type", parentType);
      form.append("parent_id", parentId);
      await api.post("/files", form, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const download = (f) => {
    const token = localStorage.getItem(TOKEN_KEY);
    window.open(`${API}/files/${f.id}/download?auth=${token}`, "_blank");
  };
  const remove = async (f) => {
    try {
      await api.delete(`/files/${f.id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 label-mono text-slate-500">
          <Paperclip size={13} /> Attachments {files.length > 0 && `(${files.length})`}
        </div>
        <button onClick={() => inputRef.current?.click()} disabled={busy}
          className="flex items-center gap-1 label-mono text-slate-500 hover:text-slate-950" data-testid="attach-upload">
          <Upload size={13} /> {busy ? "Uploading…" : "Add file"}
        </button>
        <input ref={inputRef} type="file" className="hidden" onChange={onPick} />
      </div>
      <div className="space-y-1">
        {files.length === 0 && <p className="text-xs text-muted-foreground">No files attached.</p>}
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-2 py-1.5 border-b border-border last:border-0 group">
            <Paperclip size={13} className="text-slate-400" />
            <span className="flex-1 text-sm truncate">{f.name}</span>
            {f.backend === "telegram" && <Send size={11} className="text-sky-600" title="Stored on Telegram" />}
            <span className="text-xs text-slate-400">{fmtSize(f.size)}</span>
            <button onClick={() => download(f)} className="text-slate-400 hover:text-slate-950"><Download size={14} /></button>
            <button onClick={() => remove(f)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
